import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import moment from 'moment'
import dayjs from 'dayjs'
import { initializeSelectBoxStyle } from '@/constants/form'
import { EditRoomType } from '@/hooks/use-edit-guest-rooms'
import { ApprovedStatus } from '@/models/reservation-approved-status'

type roomDateSelectProps = {
  room: EditRoomType
  roomIndex: number
  reservationCheckinDate: moment.Moment
  reservationCheckoutDate: moment.Moment
  onEditRoomStayPeriod: (selectDate: 'checkin' | 'checkout', value: string, selectIndex: number) => void
  onDeleteRoom: (selectIndex: number) => void
  isFixed: boolean
  isPreparation: boolean
}

export const RoomDateSelect: React.FC<roomDateSelectProps> = props => {
  const { t } = useTranslation()
  const [stayPeriodDates, setStayPeriodDates] = useState<string[]>([])

  const assignTypeDatesWrapperStyle = css({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: '',
    paddingBottom: '',
    paddingLeft: 12,
    borderTopLeftRadius: 5,
    borderTopRightRadius: 5,
    borderBottom: '1px solid #cccccc',
    img: { cursor: 'pointer' },
  })

  const calcStayPeriodDates = (startDate: moment.Moment, endDate: moment.Moment) => {
    if (!startDate || !endDate) {
      return []
    }
    const results: string[] = []
    const diffDay = Math.abs(startDate.clone().startOf('day').diff(endDate, 'day'))
    for (let i = 0; i <= diffDay; i++) {
      results.push(startDate.clone().add(i, 'day').format('MM-DD-YYYY'))
    }
    return results
  }

  /**
   * Determine the color of the assignment period label for each room
   */
  const getRoomDateSelectLabelColor = () => {
    if (props.isFixed) {
      return '#F5F5F5'
    }
    if (props.isPreparation) {
      return '#F2F2F2'
    }

    switch (String(props.room.assignStatus)) {
      case ApprovedStatus.Reservation:
        return '#FFFCDD'
      case ApprovedStatus.Checkin:
        return '#FEF3F0'
      case ApprovedStatus.Stay:
        return '#EFF9FF'
      case ApprovedStatus.Checkout:
        return '#F6FDF2'
      default:
        return '#FFFCDD'
    }
  }

  const assignTypeDateSelectWrapperStyle = css({
    display: 'flex',
    alignItems: 'center',
  })

  const assignTypeDateSelectStyle = css(initializeSelectBoxStyle, {
    fontSize: 11,
    width: 65,
    backgroundColor: getRoomDateSelectLabelColor(),
    border: 'none',
    backgroundImage: `url(${require('@/static/images/arrow_down_gray.svg')})`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right center',
    backgroundSize: 8,
  })

  /**
   * TODO:
   * Warning: Use the `defaultValue` or `value` props on <select> instead of setting `selected` on <option>.
   * Since the above error occurs, I want to change the implementation to not use <option> selected
   */
  useEffect(() => {
    setStayPeriodDates(calcStayPeriodDates(props.reservationCheckinDate, props.reservationCheckoutDate))
  }, [props.reservationCheckinDate, props.reservationCheckoutDate])

  return (
    <div css={assignTypeDatesWrapperStyle} style={{ background: getRoomDateSelectLabelColor() }}>
      <div css={assignTypeDateSelectWrapperStyle}>
        <select
          name="assignCheckinDate"
          value={
            props.room.assignCheckinDate
              ? dayjs(props.room.assignCheckinDate).format('MM-DD-YYYY')
              : props.reservationCheckinDate
                ? moment(props.reservationCheckinDate).format('MM-DD-YYYY')
                : undefined
          }
          onChange={e => {
            props.onEditRoomStayPeriod('checkin', e.target.value, props.roomIndex)
          }}
          disabled={props.isFixed}
          css={[assignTypeDateSelectStyle, { cursor: props.isFixed ? 'default' : 'pointer' }]}
        >
          {stayPeriodDates.map((date, index) => {
            return (
              <option key={`${props.roomIndex}-assignCheckinDate${index}`} value={date}>
                {moment(date, 'MM-DD-YYYY').format(t('MM-DD'))}
              </option>
            )
          })}
          {props.room.assignCheckinDate && !stayPeriodDates.includes(dayjs(props.room.assignCheckinDate).format('MM-DD-YYYY')) && (
            <option value={dayjs(props.room.assignCheckinDate).format('MM-DD-YYYY')} disabled>
              {moment(props.room.assignCheckinDate).format(t('MM-DD'))}
            </option>
          )}
        </select>
        <span style={{ fontSize: 8, margin: `0 2px` }}>~</span>
        <select
          name="assignCheckoutDate"
          value={
            props.room.assignCheckoutDate
              ? dayjs(props.room.assignCheckoutDate).format('MM-DD-YYYY')
              : props.reservationCheckoutDate
                ? moment(props.reservationCheckoutDate).format('MM-DD-YYYY')
                : undefined
          }
          onChange={e => {
            props.onEditRoomStayPeriod('checkout', e.target.value, props.roomIndex)
          }}
          disabled={props.isFixed}
          css={[assignTypeDateSelectStyle, { cursor: props.isFixed ? 'default' : 'pointer' }]}
        >
          {stayPeriodDates.map((date, index) => {
            return (
              <option key={`${props.roomIndex}-assignCheckoutDate${index}`} value={date}>
                {moment(date, 'MM-DD-YYYY').format(t('MM-DD'))}
              </option>
            )
          })}
          {props.room.assignCheckoutDate && !stayPeriodDates.includes(dayjs(props.room.assignCheckoutDate).format('MM-DD-YYYY')) && (
            <option value={dayjs(props.room.assignCheckoutDate).format('MM-DD-YYYY')} disabled>
              {moment(props.room.assignCheckoutDate).format(t('MM-DD'))}
            </option>
          )}
        </select>
      </div>
      <img src={require('@/static/images/delete_yellow.svg')} onClick={() => props.onDeleteRoom(props.roomIndex)} />
    </div>
  )
}
